import { useMedia, useAsync } from 'react-use';
import { SMALL } from 'lib/DeviceScreen';
import { useRef } from 'react';
import * as _ from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { fetchInfographics } from 'lib/AutolistAPI';
import Carousel from 'react-slick';
// styles
import { screen } from 'components/styles/variables';

// assets
import carouselPrev from 'public/static/images/carousel-prev.png';
import carouselNext from 'public/static/images/carousel-next.png';

type Infographic = Record<string, any>;

function useInfographics(): Infographic[] | undefined {
  const { value } = useAsync(async (): Promise<Infographic[]> => {
    const data = await fetchInfographics();
    const featuredInfographics = _.filter(data, (infographic) => infographic.is_featured === true);

    return _.sortBy(featuredInfographics, (infographic) => infographic.priority);
  });

  return value;
}

interface Props {
  onSelect(infographic: Infographic): void;
}

export default function RecentStudiesCarousel({ onSelect }: Props): JSX.Element {
  const infographics = useInfographics() || [];
  const { t } = useTranslation();
  const shouldRenderMobileCarousel = useMedia(`(max-width: ${SMALL.cssMaxWidth})`);

  return (
    <div>
      <div className="container">
        <div className="row">
          <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
            <a id="industry-studies" />
            <h2 className="recent-studies-title">{t('contentDashboard.recentIndustryStudies')}</h2>
          </div>
        </div>
      </div>
      {shouldRenderMobileCarousel ? (
        <MobileCarousel infographics={infographics} onSelect={onSelect} />
      ) : (
        <div className="container">
          <div className="row">
            <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
              <DesktopCarousel infographics={infographics} onSelect={onSelect} />
            </div>
          </div>
        </div>
      )}
      <style jsx>
        {`
          .recent-studies-title {
            margin: 0 0 15px 0;
          }

          @media (max-width: ${screen.smMin}) {
            .recent-studies-title {
              margin-bottom: 25px;
            }
          }
        `}
      </style>
    </div>
  );
}

interface CarouselProps {
  infographics: Infographic[];
  onSelect(infographic: Infographic): void;
}
const MobileCarousel = ({ infographics, onSelect }: CarouselProps): JSX.Element => (
  <div data-carousel className="mobile-carousel-container">
    <ul className="recent-studies-mobile-carousel-list">
      {infographics.map((infographic) => (
        <li
          className="recent-studies-mobile-carousel-list-item"
          key={infographic.thumbnail_url}
          onClick={(): void => onSelect(infographic)}
        >
          <img className="infographic-mobile-thumbnail" src={infographic.thumbnail_url} />
        </li>
      ))}
    </ul>
    <style jsx>
      {`
        .mobile-carousel-container {
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
        }

        .recent-studies-mobile-carousel-list {
          height: 300px;
          white-space: nowrap;
          list-style-type: none;
          padding: 0;
          margin-bottom: 0 0 15px 0;
        }

        .recent-studies-mobile-carousel-list-item {
          display: inline-block;
          height: 100%;
          margin-right: 8px;
        }

        recent-studies-mobile-carousel-list-item:last-child {
          margin-right: 0;
        }

        recent-studies-mobile-carousel-list-item .infographic-mobile-thumbnail {
          height: 100%;
          box-shadow: 3px 3px 19px -3px rgba(0, 0, 0, 0.34);
        }

        recent-studies-mobile-carousel-list-item .infographic-mobile-thumbnail:hover {
          box-shadow: 3px 3px 19px -1px rgba(0, 0, 0, 0.34);
        }
      `}
    </style>
  </div>
);

function DesktopCarousel({ onSelect, infographics }: CarouselProps): JSX.Element | null {
  const carouselRef = useRef<any>();

  function next(): void {
    const carousel = carouselRef.current;
    if (!carousel) {
      return;
    }
    carousel.slickNext();
  }

  function prev(): void {
    carouselRef.current.slickPrev();
  }

  if (_.isEmpty(infographics)) {
    return null;
  }

  const settings = {
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
  };

  return (
    <div data-carousel>
      <button onClick={prev} className="arrow arrow--prev" />
      <Carousel
        ref={(carousel): void => {
          carouselRef.current = carousel;
        }}
        {...settings}
      >
        {infographics.map((infographic) => (
          <div
            className="item"
            key={infographic.thumbnail_url}
            onClick={(): void => onSelect(infographic)}
          >
            <img src={infographic.thumbnail_url} />
          </div>
        ))}
      </Carousel>
      <button onClick={next} className="arrow arrow--next" />
      <style jsx>
        {`
          .item {
            padding: 0 5px;
            cursor: pointer;
          }

          .arrow {
            outline: 0;
            position: absolute;
            top: 183px;
            cursor: pointer;
            border: none;
            width: 27px;
            height: 57px;
            display: inline-block;
            background-color: white;
            background-size: 27px;
            background-position: center center;
            background-repeat: no-repeat;
          }

          @media (max-width: ${screen.smMax}) {
            .arrow {
              display: none;
            }
          }

          @media (min-width: ${screen.mdMin}) and (max-width: ${screen.mdMax}) {
            .arrow {
              top: 138px;
            }
          }

          @media (min-width: ${screen.lgMin}) {
            .arrow {
              top: 175px;
            }
          }

          .arrow--prev {
            background-image: url('${carouselPrev}');
            left: -14px;
          }

          .arrow--next {
            background-image: url('${carouselNext}');
            right: -14px;
          }
        `}
      </style>
      <style jsx global>{`
        // Slick overrides
        .slick-slider {
          height: 422px;
          display: inline-block;
          width: 0;
          overflow: hidden;
        }

        @media (min-width: ${screen.mdMin}) and (max-width: ${screen.mdMax}) {
          .slick-slider {
            height: 348px;
          }
        }

        @media (min-width: ${screen.mdMin}) {
          .slick-slider {
            width: 100%;
          }
        }

        .slick-slider img {
          height: 100%;
          width: 100%;
          box-shadow: 3px 3px 19px -3px rgba(0, 0, 0, 0.34);
        }

        .slick-slider img:hover {
          box-shadow: 3px 3px 19px -1px rgba(0, 0, 0, 0.34);
        }

        .slick-arrow {
          display: none !important;
        }

        .slick-slide {
          padding: 0;
          padding-right: 2px;
          padding-left: 5px;
          margin-bottom: 15px;
        }
      `}</style>
    </div>
  );
}
